import { useEffect, useMemo, useState } from 'react'
import { useGroupsWithProjects, useFolderTree } from '../lib/queries'
import type { FolderDto } from '../lib/types'

export interface MoveItem {
  kind: 'document' | 'folder'
  uuid: string
  name: string
}

interface MoveDialogProps {
  open: boolean
  /** 옮길 대상들. length 1 이면 단건, 2+ 면 multi-select. */
  items: MoveItem[]
  /** 현재 위치 (선택 기본값) */
  defaultProjectUuid?: string
  /** 현재 프로젝트 이름 (Source 표시용) */
  sourceProjectName?: string
  /** mutation 진행 중 — Move 버튼 disabled + 'Moving…' */
  pending?: boolean
  /** mutation 에러 메시지 (있으면 모달 안에 표시) */
  error?: string | null
  /** targetFolderUuid: folder uuid 또는 MOVE_TARGET_ROOT. targetProjectUuid: 항상 destination project uuid */
  onSubmit: (targetFolderUuid: string, targetProjectUuid: string) => void
  onCancel: () => void
}

interface FlatFolder { uuid: string; name: string; depth: number }

/** "프로젝트 루트" 의미하는 sentinel — onSubmit 시 호출자가 null 로 변환 */
export const MOVE_TARGET_ROOT = '__root__'

function flattenTree(tree: FolderDto[], excludeUuids: Set<string>, depth = 0): FlatFolder[] {
  const out: FlatFolder[] = []
  for (const f of tree) {
    if (excludeUuids.has(f.uuid)) continue  // 자기/자손 폴더 skip
    out.push({ uuid: f.uuid, name: f.name, depth })
    if (f.children?.length) out.push(...flattenTree(f.children, excludeUuids, depth + 1))
  }
  return out
}

export function MoveDialog({
  open, items, defaultProjectUuid, sourceProjectName,
  pending, error,
  onSubmit, onCancel,
}: MoveDialogProps) {
  const groups = useGroupsWithProjects()
  const [projectUuid, setProjectUuid] = useState<string | undefined>(defaultProjectUuid)
  const [targetFolder, setTargetFolder] = useState<string>('')
  const folders = useFolderTree(projectUuid)

  /** 자기 자신인 folder 들은 destination 에서 제외 (folder 안에 자기 못 들어감). docs 는 무관. */
  const excludeFolderUuids = useMemo(
    () => new Set(items.filter((i) => i.kind === 'folder').map((i) => i.uuid)),
    [items],
  )

  useEffect(() => {
    if (open) {
      setProjectUuid(defaultProjectUuid)
      setTargetFolder('')
      groups.refetch()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultProjectUuid])

  useEffect(() => {
    if (open && projectUuid) folders.refetch()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, projectUuid])

  if (!open || items.length === 0) return null

  const flat = flattenTree(folders.data ?? [], excludeFolderUuids)
  const selectedProject = (groups.data ?? [])
    .flatMap((g) => (g.projects ?? []).map((p) => ({ ...p, groupName: g.name })))
    .find((p) => p.uuid === projectUuid)

  const isMulti = items.length > 1
  const headerTitle = isMulti
    ? `Move ${items.length} items`
    : `Move ${items[0].kind === 'document' ? 'Document' : 'Folder'}`

  return (
    <div id="modal-move" className="modal-overlay show active" onClick={onCancel} style={{ display: 'flex' }}>
      <div className="modal" style={{ maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">{headerTitle}</div>
        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Source */}
          <section style={{ background: 'var(--bg-sidebar)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--r-md)', padding: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 6 }}>
              {isMulti ? `${items.length} items` : (items[0].kind === 'document' ? 'Document' : 'Folder')}
            </div>
            {isMulti ? (
              <ul style={{ margin: 0, padding: 0, listStyle: 'none', maxHeight: 140, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
                {items.map((it) => (
                  <li key={`${it.kind}:${it.uuid}`} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text)' }}>
                    <span style={{
                      fontSize: 10, fontWeight: 700, color: 'var(--text-muted)',
                      border: '1px solid var(--border-subtle)', borderRadius: 'var(--r-sm)',
                      padding: '0 6px', minWidth: 44, textAlign: 'center',
                    }}>{it.kind === 'document' ? 'DOC' : 'FOLDER'}</span>
                    <span style={{ wordBreak: 'break-word' }}>{it.name}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <div style={{ fontWeight: 500, color: 'var(--text)', wordBreak: 'break-word' }}>{items[0].name}</div>
            )}
            {sourceProjectName && (
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
                from project <strong>{sourceProjectName}</strong>
              </div>
            )}
          </section>

          {/* Destination */}
          <section style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.6 }}>
              Destination
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label" htmlFor="move-project-select">Project</label>
              <select
                id="move-project-select"
                value={projectUuid ?? ''}
                onChange={(e) => { setProjectUuid(e.target.value || undefined); setTargetFolder('') }}
                style={{ width: '100%' }}
              >
                <option value="">-- select project --</option>
                {(groups.data ?? []).map((g) => (
                  <optgroup key={g.uuid} label={g.name}>
                    {(g.projects ?? []).map((p) => (
                      <option key={p.uuid} value={p.uuid}>{p.name}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <label className="form-label" htmlFor="move-folder-select" style={{ marginBottom: 0 }}>Folder</label>
                <button type="button" className="btn btn-ghost btn-sm"
                        onClick={() => folders.refetch()}
                        disabled={!projectUuid || folders.isFetching}
                        style={{ fontSize: 11, padding: '2px 8px' }}>
                  {folders.isFetching ? 'Refreshing…' : 'Refresh'}
                </button>
              </div>
              <select
                id="move-folder-select"
                value={targetFolder}
                onChange={(e) => setTargetFolder(e.target.value)}
                disabled={!projectUuid}
                style={{ width: '100%' }}
              >
                <option value="">{!projectUuid
                  ? '— select a project first —'
                  : '— select destination —'}</option>
                {/* root 옵션 — 같은 프로젝트든 다른 프로젝트든 모두 가능 */}
                {projectUuid && (
                  <option value={MOVE_TARGET_ROOT}>📁 (Project root)</option>
                )}
                {flat.length === 0 && projectUuid && (
                  <option value="" disabled>
                    {folders.isFetching ? 'Loading folders…' : '— no folders in this project —'}
                  </option>
                )}
                {flat.map((f) => (
                  <option key={f.uuid} value={f.uuid}>
                    {'    '.repeat(f.depth) + (f.depth > 0 ? '└ ' : '')}{f.name}
                  </option>
                ))}
              </select>
              {targetFolder && (
                <div style={{ fontSize: 11, color: 'var(--accent)', marginTop: 4 }}>
                  ✓ Selected: <strong>{
                    targetFolder === MOVE_TARGET_ROOT
                      ? '(Project root)'
                      : flat.find((f) => f.uuid === targetFolder)?.name
                  }</strong>
                </div>
              )}
              {selectedProject && projectUuid !== defaultProjectUuid && (
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                  Cross-project move into <strong>{selectedProject.name}</strong> ({selectedProject.groupName})
                </div>
              )}
            </div>
          </section>
        </div>
        {error && (
          <div style={{ padding: '8px 16px', color: 'var(--danger)', fontSize: 13, background: 'var(--danger-soft)', margin: '0 16px', borderRadius: 'var(--r-sm)' }}>
            {error}
          </div>
        )}
        <div className="modal-footer">
          <button type="button" className="btn btn-ghost" onClick={onCancel} disabled={pending}>Cancel</button>
          <button id="btn-move" type="button" className="btn btn-primary"
                  disabled={!targetFolder || !projectUuid || pending}
                  onClick={() => {
                    if (!projectUuid) return
                    console.log('[MoveDialog] submit', { targetFolder, projectUuid, count: items.length })
                    onSubmit(targetFolder, projectUuid)
                  }}>
            {pending ? 'Moving…' : (isMulti ? `Move ${items.length} items` : 'Move')}
          </button>
        </div>
      </div>
    </div>
  )
}
